import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './Header.css';
import './Footer.css';
import './BulkSMSPage.css';
import './ContactUs.css';
import './BulkSMSLanding.css';

export const smsLandingSeo = {
  path: '/bulk-sms-kenya',
  url: 'https://peakmobile.co.ke/bulk-sms-kenya',
  title: 'Bulk SMS in Kenya | Business SMS Campaigns | Peak Mobile',
  description: 'Reach your customers with Peak Mobile bulk SMS in Kenya. Send personalised campaigns, reminders and alerts. Get a quote for your business today.',
};

export const smsLandingFaqs = [
  { question: 'What is bulk SMS, and how can my business use it?', answer: 'Bulk SMS lets you send a text message to many contacts in one campaign. Kenyan businesses can use it for promotions, appointment reminders, order updates, member notices and customer alerts. With Peak Mobile, you can manage campaigns through a web platform or connect messaging to your own software using an API.' },
  { question: 'Do my customers need an app or internet connection?', answer: 'No. SMS arrives in the phone’s regular messaging inbox, so recipients do not need to download an app or have mobile data. They do need an active mobile number, a compatible handset and mobile network coverage.' },
  { question: 'How much does bulk SMS cost?', answer: 'Your quote depends on your messaging volume and requirements. Tell us approximately how many messages you plan to send and our team will help you choose a suitable package. We will confirm pricing and any setup requirements with you before you get started.' },
  { question: 'Can I personalise and schedule my messages?', answer: 'Yes. Peak Mobile supports personalised messages, contact list management and scheduled SMS campaigns. You can plan an announcement in advance and tailor your message to the audience you want to reach.' },
  { question: 'Can I connect bulk SMS to my website or application?', answer: 'Yes. API access is available for developers who want to connect SMS to their own systems. Tell our team about your application and the messages you need to send so we can discuss the integration with you.' },
  { question: 'How do I get started with Peak Mobile?', answer: 'Request a bulk SMS quote using the form on this page. Share your business details and estimated monthly SMS volume, and our team will contact you to discuss your campaign and setup. Already have an account? You can sign in to the Peak Mobile platform below.' },
];

export const smsLandingSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization', '@id': 'https://peakmobile.co.ke/#organization',
      name: 'Peak Mobile', url: 'https://peakmobile.co.ke/',
      logo: 'https://peakmobile.co.ke/images/logo1.svg',
      telephone: '+254759422480', email: 'chat@peakmobile.co.ke',
      address: { '@type': 'PostalAddress', streetAddress: 'The Promenade, 3rd Floor, General Mathenge Drive', addressLocality: 'Nairobi', addressCountry: 'KE' },
    },
    {
      '@type': 'Service', '@id': `${smsLandingSeo.url}#service`,
      name: 'Bulk SMS in Kenya', serviceType: 'Business bulk SMS messaging',
      description: smsLandingSeo.description, url: smsLandingSeo.url,
      areaServed: { '@type': 'Country', name: 'Kenya' },
      provider: { '@id': 'https://peakmobile.co.ke/#organization' },
    },
    {
      '@type': 'FAQPage', '@id': `${smsLandingSeo.url}#faqs`,
      mainEntity: smsLandingFaqs.map(({ question, answer }) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })),
    },
  ],
};

export function trackSmsAction(action, placement) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, { event_category: 'bulk_sms_landing', placement });
  }
}

function QuoteLink({ children = 'Get my bulk SMS quote', placement, className = '' }) {
  return <a className={`btn-primary-sms smslp-button ${className}`} href="#sms-quote" onClick={() => trackSmsAction('sms_quote_click', placement)}>{children}<span aria-hidden="true">→</span></a>;
}

function LandingMetadata() {
  useEffect(() => {
    document.body.classList.add('sms-landing-active');
    document.title = smsLandingSeo.title;
    const changes = [];
    const setTag = (selector, tagName, attributes) => {
      let element = document.head.querySelector(selector);
      const existed = Boolean(element && !element.hasAttribute('data-sms-landing'));
      const previous = element ? element.outerHTML : null;
      if (!element) {
        element = document.createElement(tagName);
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      changes.push(() => {
        if (existed) element.outerHTML = previous;
        else element.remove();
      });
    };
    const description = document.head.querySelector('meta[name="description"]');
    if (description) description.content = smsLandingSeo.description;
    else setTag('meta[name="description"]', 'meta', { name: 'description', content: smsLandingSeo.description });
    setTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: smsLandingSeo.url, 'data-sms-landing': 'true' });
    const meta = {
      'og:title': smsLandingSeo.title, 'og:description': smsLandingSeo.description,
      'og:url': smsLandingSeo.url, 'og:type': 'website', 'og:site_name': 'Peak Mobile',
      'og:locale': 'en_KE', 'og:image': 'https://peakmobile.co.ke/images/sms-service.jpg',
      'og:image:alt': 'Peak Mobile business messaging',
    };
    Object.entries(meta).forEach(([property, content]) => setTag(`meta[property="${property}"]`, 'meta', { property, content, 'data-sms-landing': 'true' }));
    Object.entries({ 'twitter:card': 'summary_large_image', 'twitter:title': smsLandingSeo.title, 'twitter:description': smsLandingSeo.description, 'twitter:image': meta['og:image'] }).forEach(([name, content]) => setTag(`meta[name="${name}"]`, 'meta', { name, content, 'data-sms-landing': 'true' }));
    let schema = document.getElementById('sms-landing-schema');
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'sms-landing-schema';
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(smsLandingSchema);
    return () => {
      document.body.classList.remove('sms-landing-active');
      document.title = 'Peak Mobile';
      const descriptionTag = document.head.querySelector('meta[name="description"]');
      if (descriptionTag) descriptionTag.content = 'Peak Mobile Website';
      changes.reverse().forEach(restore => restore());
      schema.remove();
    };
  }, []);
  return null;
}

function QuoteForm() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const successRef = useRef(null);
  const configured = Boolean(process.env.REACT_APP_EMAILJS_SERVICE_ID && process.env.REACT_APP_EMAILJS_TEMPLATE_ID && process.env.REACT_APP_EMAILJS_PUBLIC_KEY && process.env.REACT_APP_RECAPTCHA_SITE_KEY);

  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  async function submitQuote(event) {
    event.preventDefault();
    if (status === 'sending') return;
    if (!captchaToken) {
      setError('Please complete the verification below before sending your request.');
      return;
    }
    const form = event.currentTarget;
    const values = new FormData(form);
    const fields = ['name', 'email', 'phone', 'company', 'volume'];
    if (fields.some(field => !String(values.get(field) || '').trim())) {
      setError('Please complete all the fields so we can prepare your quote.');
      return;
    }
    setStatus('sending');
    setError('');
    const params = new URLSearchParams(window.location.search);
    const attribution = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid']
      .filter(key => params.has(key))
      .map(key => `${key}: ${params.get(key).slice(0, 300)}`).join('\n');
    try {
      const emailjs = await import('@emailjs/browser');
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: values.get('name').trim(), from_email: values.get('email').trim(),
          phoneNumber: values.get('phone').trim(), company_name: values.get('company').trim(),
          industry: 'Bulk SMS enquiry',
          message: `BULK SMS QUOTE REQUEST\nEstimated monthly SMS volume: ${values.get('volume')}\nLanding page: ${smsLandingSeo.url}${attribution ? `\nCampaign details:\n${attribution}` : ''}`,
          to_email: 'tedaringo628@gmail.com',
          'g-recaptcha-response': captchaToken,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      trackSmsAction('generate_lead', 'quote_form');
    } catch {
      setStatus('idle');
      setError('Your request could not be sent. Please try again, or call us on +254 759 422 480.');
    } finally {
      captchaRef.current?.reset();
      setCaptchaToken('');
    }
  }

  if (status === 'success') return (
    <div className="contact-form-card smslp-success" ref={successRef} tabIndex="-1" role="status">
      <p className="form-success">Your bulk SMS quote request has been sent. Our team will contact you using the details you shared.</p>
      <p>You can also <a href="tel:+254759422480">call +254 759 422 480</a>.</p>
    </div>
  );

  if (!configured) return (
    <div className="contact-form-card smslp-form-fallback">
      <p>Call or email our team with your business name and estimated SMS volume to request a quote.</p>
      <a className="btn-primary-sms smslp-button" href="tel:+254759422480">Call +254 759 422 480</a>
      <a href="mailto:chat@peakmobile.co.ke?subject=Bulk%20SMS%20quote%20request">Email chat@peakmobile.co.ke</a>
    </div>
  );

  return (
    <form className="contact-form-card smslp-form" onSubmit={submitQuote} method="post" aria-label="Request a bulk SMS quote" aria-busy={status === 'sending'}>
      <fieldset disabled={status === 'sending'}>
        <div className="smslp-form-grid">
          <label className="form-group" htmlFor="sms-name">Your name<input id="sms-name" name="name" autoComplete="name" placeholder="Full name" maxLength="120" required /></label>
          <label className="form-group" htmlFor="sms-company">Business name<input id="sms-company" name="company" autoComplete="organization" placeholder="Your company" maxLength="160" required /></label>
          <label className="form-group" htmlFor="sms-email">Work email<input id="sms-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" maxLength="254" required /></label>
          <label className="form-group" htmlFor="sms-phone">Phone number<input id="sms-phone" name="phone" type="tel" autoComplete="tel" placeholder="e.g. 0712 345 678" maxLength="30" pattern={'[+0-9\\(\\) .\\-]{7,30}'} required /></label>
        </div>
        <label className="form-group" htmlFor="sms-volume">Estimated SMS per month<select id="sms-volume" name="volume" defaultValue="" required>
          <option value="" disabled>Select your monthly volume</option>
          <option>Under 25,000</option><option>25,000–125,000</option><option>125,001–524,999</option><option>525,000 or more</option><option>Help me decide</option>
        </select></label>
        <label className="smslp-consent"><input type="checkbox" required name="consent" /><span>I agree to be contacted about my bulk SMS enquiry. See our <a href="/privacy">privacy policy</a>.</span></label>
        <div className="smslp-captcha"><ReCAPTCHA ref={captchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={token => { setCaptchaToken(token || ''); setError(''); }} onExpired={() => setCaptchaToken('')} onErrored={() => { setCaptchaToken(''); setError('Verification could not load. Please try again, or call +254 759 422 480.'); }} /></div>
        {error && <p className="form-error smslp-error" role="alert">{error}</p>}
        <button type="submit" className="btn-primary-sms smslp-button">{status === 'sending' ? 'Sending your request…' : 'Get my bulk SMS quote'}<span aria-hidden="true">→</span></button>
      </fieldset>
    </form>
  );
}

export default function BulkSMSLanding() {
  return (
    <div className="sms-landing bulk-sms-page">
      <LandingMetadata />
      <a className="smslp-skip" href="#sms-main">Skip to content</a>
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <a href="/" aria-label="Peak Mobile homepage"><img src="/images/logo1.svg" alt="Peak Mobile" width="156" height="51" /></a>
          </div>
          <nav aria-label="Bulk SMS page"><QuoteLink placement="header">Get a quote</QuoteLink></nav>
        </div>
      </header>

      <main id="sms-main">
        <section className="hero-section smslp-hero" aria-labelledby="sms-hero-heading">
          <h1 id="sms-hero-heading">Bulk SMS in Kenya</h1>
          <p className="subtitle">Send promotions, reminders and customer updates with Peak Mobile’s bulk SMS platform.</p>
          <QuoteLink placement="hero" />
        </section>

        <section className="why-section" aria-label="Bulk SMS platform">
          <div className="why-container smslp-overview">
            <div className="why-image">
              <img src="/images/bulksms.svg" alt="Bulk SMS messaging" width="500" height="400" />
            </div>
            <div className="why-text">
              <p>Send bulk SMS to your customers through our web platform or connect messaging to your own application with our API.</p>
              <p>Upload and organise your contacts, personalise your messages, and send them immediately or schedule them in advance. Check delivery reports to see the status of your campaign.</p>
              <p>Use SMS for sales promotions, appointment reminders, order confirmations, member notices and other business updates.</p>
              <QuoteLink placement="overview" />
            </div>
          </div>
        </section>

        <section id="sms-quote" className="contact-form-section" aria-label="Request a bulk SMS quote">
          <div className="contact-container smslp-quote-grid">
            <div className="contact-form-card smslp-quote-copy">
              <p>Share your business details and estimated monthly SMS volume. Our team will contact you with pricing and help you set up your account.</p>
              <p>You can also contact us directly:</p>
              <p><a href="tel:+254759422480" onClick={() => trackSmsAction('sms_call_click', 'quote_section')}>+254 759 422 480</a><br /><a href="mailto:chat@peakmobile.co.ke">chat@peakmobile.co.ke</a></p>
              <p>Monday–Friday, 8:00 AM–5:00 PM EAT</p>
            </div>
            <div className="smslp-quote-form">
              <noscript><div className="contact-form-card smslp-form-fallback"><p>For a bulk SMS quote, call <a href="tel:+254759422480">+254 759 422 480</a> or email <a href="mailto:chat@peakmobile.co.ke?subject=Bulk%20SMS%20quote%20request">chat@peakmobile.co.ke</a>.</p></div></noscript>
              <QuoteForm />
            </div>
          </div>
        </section>

        <section id="sms-faqs" className="smslp-faqs" aria-label="Bulk SMS questions">
          {smsLandingFaqs.map(({ question, answer }) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
          <p className="smslp-signin"><a href="https://app.peakmobile.co.ke/signin" onClick={() => trackSmsAction('sms_signin_click', 'existing_customer')}>Sign in to send bulk SMS <span aria-hidden="true">→</span></a></p>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-bottom">
          <div className="footer-container">
            <p className="copyright">© {new Date().getFullYear()} Peak Mobile. All rights reserved.</p>
            <div className="legal-links"><a href="/privacy">Privacy policy</a><a href="tel:+254759422480">+254 759 422 480</a></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
